import { supabase } from '../lib/supabase';
import { getIconName } from '../utils/industryConfettiConfig';

export const industryService = {
  /**
   * Get all active industries for public use (signup form)
   */
  async getAllActive() {
    try {
      const { data, error } = await supabase?.from('company_industries')?.select('*')?.eq('is_active', true)?.order('sort_order');

      if (error) throw error;

      return data?.map(industry => ({
        id: industry?.id,
        name: industry?.name,
        naicsCode: industry?.naics_code,
        description: industry?.description,
        icon: industry?.icon,
        color: industry?.color,
        isActive: industry?.is_active,
        sortOrder: industry?.sort_order,
        confettiIcons: industry?.confetti_icons || [],
        createdAt: industry?.created_at,
        updatedAt: industry?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching active industries:', error);
      throw error;
    }
  },

  /**
   * Get all industries (for super admin management)
   */
  async getAll() {
    try {
      const { data, error } = await supabase?.from('company_industries')?.select('*')?.order('sort_order');

      if (error) throw error;

      return data?.map(industry => ({
        id: industry?.id,
        name: industry?.name,
        naicsCode: industry?.naics_code,
        description: industry?.description,
        icon: industry?.icon,
        color: industry?.color,
        isActive: industry?.is_active,
        sortOrder: industry?.sort_order,
        confettiIcons: industry?.confetti_icons || [],
        createdAt: industry?.created_at,
        updatedAt: industry?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching all industries:', error);
      throw error;
    }
  },

  /**
   * Get single industry by ID
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('company_industries')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        naicsCode: data?.naics_code,
        description: data?.description,
        icon: data?.icon,
        color: data?.color,
        isActive: data?.is_active,
        sortOrder: data?.sort_order,
        confettiIcons: data?.confetti_icons || [],
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching industry:', error);
      throw error;
    }
  },

  /**
   * Create new industry
   */
  async create(industry) {
    try {
      // Serialize confetti icons to store icon names instead of components
      const confettiIconsData = industry?.confettiIcons?.map(iconData => {
        const iconName = getIconName(iconData?.icon);
        return {
          iconName: iconName,
          color: iconData?.color,
          label: iconData?.label
        };
      }) || [];

      const { data, error } = await supabase?.from('company_industries')?.insert({
          name: industry?.name,
          naics_code: industry?.naicsCode,
          description: industry?.description,
          icon: industry?.icon || 'Briefcase',
          color: industry?.color || '#3b82f6',
          sort_order: industry?.sortOrder || 0,
          confetti_icons: confettiIconsData
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        naicsCode: data?.naics_code,
        description: data?.description,
        icon: data?.icon,
        color: data?.color,
        isActive: data?.is_active,
        sortOrder: data?.sort_order,
        confettiIcons: data?.confetti_icons || [],
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating industry:', error);
      throw error;
    }
  },

  /**
   * Update existing industry
   */
  async update(id, updates) {
    try {
      // Serialize confetti icons to store icon names instead of components
      const confettiIconsData = updates?.confettiIcons?.map(iconData => {
        const iconName = getIconName(iconData?.icon);
        return {
          iconName: iconName,
          color: iconData?.color,
          label: iconData?.label
        };
      }) || [];

      // Build update object with only provided fields
      const updateData = {};
      
      if (updates?.name !== undefined) updateData.name = updates?.name;
      if (updates?.naicsCode !== undefined) updateData.naics_code = updates?.naicsCode;
      if (updates?.description !== undefined) updateData.description = updates?.description;
      if (updates?.icon !== undefined) updateData.icon = updates?.icon;
      if (updates?.color !== undefined) updateData.color = updates?.color;
      if (updates?.isActive !== undefined) updateData.is_active = updates?.isActive;
      if (updates?.sortOrder !== undefined) updateData.sort_order = updates?.sortOrder;
      if (updates?.confettiIcons !== undefined) updateData.confetti_icons = confettiIconsData;

      const { data, error } = await supabase?.from('company_industries')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        naicsCode: data?.naics_code,
        description: data?.description,
        icon: data?.icon,
        color: data?.color,
        isActive: data?.is_active,
        sortOrder: data?.sort_order,
        confettiIcons: data?.confetti_icons || [],
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating industry:', error);
      throw error;
    }
  },

  /**
   * Delete industry
   */
  async delete(id) {
    try {
      const { error } = await supabase?.from('company_industries')?.delete()?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting industry:', error);
      throw error;
    }
  },

  /**
   * Toggle industry active status
   */
  async toggleStatus(id, currentStatus) {
    try {
      const { data, error } = await supabase?.from('company_industries')?.update({ is_active: !currentStatus })?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        isActive: data?.is_active
      };
    } catch (error) {
      console.error('Error toggling industry status:', error);
      throw error;
    }
  }
};